import React from 'react';
import { LinearProgress } from '@mui/material';
import { withRouter } from 'react-router-dom';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    TextFilterDefaults,
    booleanCellRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import CommonContext from '../Common';
import { ApiRoutes } from '../ApiRoutes';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import { util } from '../Util';
import DivisionForm from './DivisionForm';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import './DivisionIndex.scss';
import { PageWrap } from '../common/forms/FormElements';

class DivisionIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.divisionForm = React.createRef();

        this.state = {
            loading: true,
            divisions: [],
            showDivisionForm: false,
            crews: [],
        };

        this.onAddDivision = this.onAddDivision.bind(this);
        this.onEditDivision = this.onEditDivision.bind(this);
        this.onDivisionFormClosed = this.onDivisionFormClosed.bind(this);
        this.yesNoCellRenderer = this.yesNoCellRenderer.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentWillUnmount() {
        return (this.setState = (state, callback) => {});
    }

    yesNoCellRenderer = booleanCellRenderer;

    onAddDivision = () => {
        this.context.setFormOpened(true);
        this.divisionForm.current.openDivision();
    };

    onEditDivision = (divisionId) => {
        this.context.setFormOpened(true);
        this.divisionForm.current.openDivision(divisionId);
    };

    onDivisionFormClosed = () => {
        this.context.setFormOpened(false);
        this.state.gridOptions.refresh();
    };

    getColor = (params) => {
        if (!params || !params.value) {
            return null;
        }

        return (
            <div
                className="full-size"
                style={{ background: params.value }}
            ></div>
        );
    };

    renderCrews = (params) => {
        const { crews } = this.state;

        const crewIds = params.value;

        if (!crewIds || !crewIds.length) {
            return null;
        }

        return crews
            .filter((c) => crewIds.includes(c.id))
            .map((c) => c.name)
            .join(', ');
    };

    async populateState() {
        const { divisionId } = this.props;

        const [crews] = await Promise.all([
            util.fetch.js(ApiRoutes.crew.all()),
        ]);

        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
        };

        gridOptions.columnDefs = [
            {
                flex: 0,
                width: 80,
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'name',
                headerName: 'Division Name',
                sortable: true,
                field: 'name',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: 'nameRenderer',
                cellRendererParams: {
                    clicked: this.onEditDivision,
                    nameField: 'name',
                    idField: 'id',
                    title: 'View this Division',
                },
            },
            {
                colId: 'crewName',
                headerName: 'Crews',
                sortable: true,
                field: 'crewIds',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: this.renderCrews,
            },
            {
                colId: 'colorHex',
                headerName: 'Color',
                sortable: false,
                field: 'colorHex',
                cellRenderer: this.getColor,
            },
            {
                colId: 'inactive',
                headerName: 'Inactive',
                sortable: false,
                field: 'inactive',
                cellRenderer: this.yesNoCellRenderer,
            },
        ];

        const dataSource = createDataSource(
            ApiRoutes.division.search(),
            gridOptions
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            crews,
            divisionId,
        });

        const { search } = { ...this.props.location };

        if (search) {
            const paramDivisionId = new URLSearchParams(search).get(
                'divisionId'
            );

            if (paramDivisionId) {
                this.onEditDivision(paramDivisionId);
            }
        }
    }

    toggleDivisionForm = () => {
        const { showDivisionForm } = this.state;

        this.setState({ showDivisionForm: !showDivisionForm });
    };

    render() {
        const {
            rowData,
            gridOptions,
            showDivisionForm,
            loading,
            gridApi,
            dataSource,
            gridStatus,
        } = this.state;

        if (loading) {
            return <LinearProgress variant="indeterminate" color="secondary" />;
        }

        return (
            <PageWrap>
                <DataGridToolbar
                    entity="division"
                    gridApi={gridApi}
                    dataSource={dataSource}
                    onAdd={this.onAddDivision}
                    addLabel="Add Division"
                />
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    onNameCellClicked={this.onEditDivision}
                    gridStatus={gridStatus}
                />
                <DivisionForm
                    show={showDivisionForm}
                    toggleShow={this.toggleDivisionForm}
                    ref={this.divisionForm}
                    onClose={this.onDivisionFormClosed}
                />
            </PageWrap>
        );
    }
}

export default withRouter(DivisionIndex);
